import { z } from 'zod';
import type { RhinoAttribute } from '../index';

const TRANSFORMABLE_TYPES = ['string', 'text', 'integer', 'number', 'decimal'];

export const zodDefaultFromAttribute = (attribute: RhinoAttribute): unknown => {
  // An explicit model default (e.g. a boolean column declared `default: true`,
  // such as Contact#primary) wins over the per-type fallback below. Restores
  // the prior yup behavior, where yupValidatorsFromAttribute applied
  // `.default(attribute.default)` before falling back to the type default.
  if (attribute.default != null) return attribute.default;

  switch (attribute.type) {
    case 'array':
      return [];
    case 'boolean':
      return false;
    case 'reference':
      return null;
    case 'integer':
    case 'number':
      return '';
    case 'string':
      // The default value for an enum is null not the empty string
      // because this is how the select component works
      if (attribute.enum) return null;

      switch (attribute.format) {
        case 'datetime':
        case 'date':
        case 'time':
          return null;
        default:
          return '';
      }
    case 'text':
    default:
      return '';
  }
};

// Filter schema: everything optional/nullable; empty -> null; -1 ref -> undefined.
export const zodFiltersFromAttribute = (
  attribute: RhinoAttribute
): z.ZodType => {
  return z.unknown().transform((value) => {
    // Mirrors yup's transformFilterEmptyString: '' -> null on transformable
    // types (the filter path does NOT do NaN -> null; that is validator-only).
    if (TRANSFORMABLE_TYPES.includes(attribute.type) && value === '') {
      return null;
    }
    if (
      attribute.type === 'reference' &&
      (value === -1 || value === '-1' || value == null)
    ) {
      return undefined;
    }
    return value;
  });
};
